import React, { useEffect, useMemo, useRef, useState } from 'react';
import { __t } from '../../../../../base/utils/t';
import { getRefById, RefMap, scrollToError } from '../../../../../base/utils';
import { useAppDispatch, useAppSelector } from '../../../../../base/hooks';
import { vacancyFormActions } from '../../redux/slices/vacancyFormSlice';

import { Icon } from '../../../../../base/components/Icon';
import { CheckboxWithContent } from '../../../../../base/components/Forms/CheckboxWithContent';
import { NumberInput } from '../../../../../base/components/Forms/NumberInput';
import { ErrorMessage } from '../../../../../base/components/Forms/ErrorMessage';

import { SpecialityAutocomplete } from '../SpecialityAutocomplete';
import { VacancySuitableFor } from '../../enums';

import { Input } from '../../../../../base/components/Forms/Input';
import { VACANCY_NAME_MAX_LENGTH } from '../../constants';

import './styles.scss';
import { CityAutocomplete } from '../../../../../base/components/CityAutocomplete';
import { employerAppActions } from '../../../../redux/slices/employerAppSlice';
import { City } from '../../../../../base/interfaces/config';
import { RadioSelectWithContent } from '../../../../../base/components/Forms/RadioSelectWithContent';
import { Label } from '../../../../../base/components/Label';
import { ClickTooltip } from '../../../../../base/components/ClickTooltip';
import { QuickApplyExplanationModal } from '../QuickApplyExplanationModal';

export const VacancyBaseInfoStep = () => {
    const dispatch = useAppDispatch();
    const {
        fields: {
            name,
            suitableForCouple,
            suitableForMen,
            suitableForStudents,
            suitableForWomen,
            workersCount,
            city,
            isQuickApplyAvailable,
        },
        errors,
        shouldScrollToError,
    } = useAppSelector((state) => state.vacancyForm);
    const citySearchOptions = useAppSelector((state) => state.employerApp.citySearchOptions);

    const [showQuickApplyModal, setShowQuickApplyModal] = useState(false);
    const [shouldForceCloseQuickApplyTooltip, setShouldForceCloseQuickApplyTooltip] = useState(false);

    const refMap: RefMap[] = [
        {
            id: 'name',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'specialityId',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'workersCount',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'applyType',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'suitableFor',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'cityId',
            ref: useRef<HTMLDivElement>(null),
        },
    ];

    useEffect(() => {
        if (errors && shouldScrollToError) {
            scrollToError(errors, refMap);
            dispatch(vacancyFormActions.setShouldScrollToError(false));
        }
    }, [refMap, errors, shouldScrollToError]);

    const inputChangeHandler = (id: string, value: unknown) => {
        dispatch(vacancyFormActions.setField({ id, value }));
    };

    const checkboxGroupChangeHandler = (id: string, value: unknown) => {
        dispatch(vacancyFormActions.setField({ id, value, errorGroupName: 'suitableFor' }));
    };

    const quickApplyChangeHandler = (id: string, value: string) => {
        switch (value) {
            case 'cvOnly':
                dispatch(vacancyFormActions.setField({ id: 'isQuickApplyAvailable', value: false }));
                return;
            case 'quickApply':
                dispatch(vacancyFormActions.setField({ id: 'isQuickApplyAvailable', value: true }));
                return;
        }
    };

    const hideQuickApplyModalHandler = () => setShowQuickApplyModal(false);

    const showQuickApplyModalHandler = () => {
        setShowQuickApplyModal(true);
        setShouldForceCloseQuickApplyTooltip(true);
        setTimeout(() => {
            setShouldForceCloseQuickApplyTooltip(false);
        }, 500);
    };

    const tagsHaveError = useMemo((): boolean => !!errors?.suitableFor && !!errors?.suitableFor.length, [errors]);

    return (
        <>
            <div className='vacancy-form vacancy-form-step-2'>
                <div className='vacancy-form-step-2__wrapper'>
                    <div
                        className='scroll-margin-for-fixed-header'
                        ref={getRefById('name', refMap)?.ref}
                        style={{ marginBottom: '8px' }}
                    >
                        <p className='vacancy-form__label'>{__t('Название вакансии')}</p>

                        <Input
                            value={name}
                            placeholder={__t('Придумайте название')}
                            maxLength={VACANCY_NAME_MAX_LENGTH}
                            errors={errors?.name}
                            onChange={(value: string) => inputChangeHandler('name', value)}
                        />
                    </div>

                    <div
                        className='scroll-margin-for-fixed-header'
                        ref={getRefById('specialityId', refMap)?.ref}
                        style={{ marginBottom: '8px' }}
                    >
                        <p className='vacancy-form__label'>{__t('Название специальности')}</p>

                        <SpecialityAutocomplete />
                    </div>

                    <div
                        className='vacancy-form__item scroll-margin-for-fixed-header'
                        ref={getRefById('workersCount', refMap)?.ref}
                    >
                        <p className='vacancy-form__label'>{__t('Сколько работников ищете?')}</p>
                        <NumberInput
                            value={workersCount}
                            minValue='1'
                            maxValue='999'
                            onChange={(value: string) => inputChangeHandler('workersCount', value)}
                        />
                    </div>

                    <div
                        className='vacancy-form__item scroll-margin-for-fixed-header'
                        ref={getRefById('applyType', refMap)?.ref}
                    >
                        <p className='vacancy-form__label'>{__t('Работник может откликнуться без анкеты?')}</p>

                        <fieldset className='vacancy-form-step-2__checkbox-wrap'>
                            <RadioSelectWithContent
                                id='applyType'
                                value={'cvOnly'}
                                checked={!isQuickApplyAvailable}
                                onChange={quickApplyChangeHandler}
                                className='vacancy-form-step-2__checkbox-item'
                            >
                                <div className='vacancy-form-step-2__checkbox-item-content'>
                                    <Icon
                                        className='vacancy-form-step-2__checkbox-item-icon'
                                        icon='file-user-thin'
                                        size='40'
                                    />
                                    <span className='vacancy-form-step-2__checkbox-item-text'>
                                        {__t('Анкета обязательно')}
                                    </span>
                                </div>
                            </RadioSelectWithContent>

                            <RadioSelectWithContent
                                id='applyType'
                                value={'quickApply'}
                                checked={isQuickApplyAvailable}
                                onChange={quickApplyChangeHandler}
                                className='vacancy-form-step-2__checkbox-item'
                            >
                                <div className='vacancy-form-step-2__checkbox-item-content'>
                                    <ClickTooltip
                                        align='left'
                                        wrapperClassName='vacancy-form-step-2__quick-apply-tooltip-wrapper'
                                        className='vacancy-form-step-2__quick-apply-tooltip-component'
                                        activeButton={
                                            <Label type='danger' className='vacancy-form-step-2__new-label'>
                                                {__t('New')}
                                            </Label>
                                        }
                                        forceClose={shouldForceCloseQuickApplyTooltip}
                                    >
                                        <>
                                            <p className='vacancy-form-step-2__quick-apply-tooltip-text'>
                                                {__t(
                                                    'Эта функция полезна, если вы хотите получить больше откликов, но с минимальной информацией о работнике.',
                                                )}
                                            </p>
                                            <div
                                                onClick={showQuickApplyModalHandler}
                                                className='vacancy-form-step-2__quick-apply-tooltip-link'
                                            >
                                                {__t('Подробнее')}
                                            </div>
                                        </>
                                    </ClickTooltip>
                                    <Icon
                                        className='vacancy-form-step-2__checkbox-item-icon'
                                        icon='bolt-thin'
                                        size='40'
                                    />
                                    <span className='vacancy-form-step-2__checkbox-item-text'>
                                        {__t('Отклик без анкеты')}
                                    </span>
                                </div>
                            </RadioSelectWithContent>
                        </fieldset>
                    </div>

                    <div
                        className='vacancy-form__item scroll-margin-for-fixed-header'
                        ref={getRefById('suitableFor', refMap)?.ref}
                    >
                        <p className='vacancy-form__label'>{__t('Для кого подходит эта работа?')}</p>

                        <div className='vacancy-form-step-2__checkbox-wrap'>
                            <CheckboxWithContent
                                id={VacancySuitableFor.Man}
                                className='vacancy-form-step-2__checkbox-item'
                                checked={suitableForMen}
                                hasError={tagsHaveError}
                                onChange={checkboxGroupChangeHandler}
                            >
                                <div className='vacancy-form-step-2__checkbox-item-content'>
                                    <Icon
                                        className='vacancy-form-step-2__checkbox-item-icon'
                                        icon='person-thin'
                                        size='40'
                                    />
                                    <span className='vacancy-form-step-2__checkbox-item-text'>{__t('Мужчинам')}</span>
                                </div>
                            </CheckboxWithContent>

                            <CheckboxWithContent
                                id={VacancySuitableFor.Woman}
                                className='vacancy-form-step-2__checkbox-item'
                                checked={suitableForWomen}
                                hasError={tagsHaveError}
                                onChange={checkboxGroupChangeHandler}
                            >
                                <div className='vacancy-form-step-2__checkbox-item-content'>
                                    <Icon
                                        className='vacancy-form-step-2__checkbox-item-icon'
                                        icon='person-dress-thin'
                                        size='40'
                                    />
                                    <span className='vacancy-form-step-2__checkbox-item-text'>{__t('Женщинам')}</span>
                                </div>
                            </CheckboxWithContent>

                            <CheckboxWithContent
                                id={VacancySuitableFor.Couple}
                                className='vacancy-form-step-2__checkbox-item'
                                checked={suitableForCouple}
                                hasError={tagsHaveError}
                                onChange={checkboxGroupChangeHandler}
                            >
                                <div className='vacancy-form-step-2__checkbox-item-content'>
                                    <Icon
                                        className='vacancy-form-step-2__checkbox-item-icon'
                                        icon='people-thin'
                                        size='40'
                                    />
                                    <span className='vacancy-form-step-2__checkbox-item-text'>
                                        {__t('Семейным парам')}
                                    </span>
                                </div>
                            </CheckboxWithContent>

                            <CheckboxWithContent
                                id={VacancySuitableFor.Student}
                                className='vacancy-form-step-2__checkbox-item'
                                checked={suitableForStudents}
                                hasError={tagsHaveError}
                                onChange={checkboxGroupChangeHandler}
                            >
                                <div className='vacancy-form-step-2__checkbox-item-content'>
                                    <Icon
                                        className='vacancy-form-step-2__checkbox-item-icon'
                                        icon='graduation-cap-thin'
                                        size='40'
                                    />
                                    <span className='vacancy-form-step-2__checkbox-item-text'>{__t('Студентам')}</span>
                                </div>
                            </CheckboxWithContent>

                            <ErrorMessage errors={errors?.suitableFor} />
                        </div>
                    </div>

                    <div
                        className='vacancy-form__item scroll-margin-for-fixed-header'
                        ref={getRefById('cityId', refMap)?.ref}
                    >
                        <p className='vacancy-form__label'>{__t('Населенный пункт работы')}</p>

                        <CityAutocomplete
                            appType='employer'
                            options={citySearchOptions}
                            selectedOption={city}
                            errors={errors?.cityId}
                            onSearch={(value: string) => dispatch(employerAppActions.citySearchRequest(value))}
                            setSearchOptions={(options: City[] | null) =>
                                dispatch(employerAppActions.citySearchSuccess(options))
                            }
                            onSelect={(value: City | null) =>
                                dispatch(vacancyFormActions.setField({ id: 'city', value, errorGroupName: 'cityId' }))
                            }
                        />
                    </div>
                </div>
            </div>
            <QuickApplyExplanationModal visible={showQuickApplyModal} visibilityHandler={hideQuickApplyModalHandler} />
        </>
    );
};
